import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, GeoJSON, ImageOverlay, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import envConfig from '../envConfig';
import StationGraph from './StationGraph';
import Product3Legend from './Product3Legend';
import bgImage from '../assets/images/layers.png';

// Tile layer options
const tileLayers = {
    osm: {
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '&copy; OpenStreetMap contributors',
        icon: '../assets/images/StreetMap.png',
        label: 'Street Map',
    },
    terrain: {
        url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
        attribution: '&copy; OpenTopoMap contributors',
        icon: '../assets/images/TerrainMap.png',
        label: 'Terrain',
    },
    satellite: {
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attribution: '&copy; Esri & NASA Earthstar Geographics',
        icon: '../assets/images/SatellitesMap.png',
        label: 'Satellite',
    },
};

const FitBounds = ({ geoJson, dataType }) => {
    const map = useMap();

    useEffect(() => {
        if (geoJson && geoJson.features.length > 0) {
            const geoJsonLayer = L.geoJson(geoJson);
            const bounds = geoJsonLayer.getBounds();
            if (bounds.isValid()) {
                map.fitBounds(bounds, { padding: [20, 20] });
                if (dataType !== 'exceed') {
                    setTimeout(() => {
                        map.panBy([200, 0]);
                    }, 200);
                }
                setTimeout(() => {
                    map.setZoom(map.getZoom() + 1);
                }, 300);
            }
        }
    }, [geoJson, map, dataType]);

    return null;
};

// Modified component to adjust zoom control position
const AdjustZoomControl = ({ dataType }) => {
    const map = useMap();

    useEffect(() => {
        map.eachLayer((layer) => {
            if (layer instanceof L.Control.Zoom) {
                map.removeControl(layer);
            }
        });

        const zoomControl = L.control.zoom({
            position: 'topleft',
        }).addTo(map);

        const zoomControlElement = map.getContainer().querySelector('.leaflet-control-zoom');
        if (zoomControlElement) {
            zoomControlElement.style.top = dataType === 'exceed' ? '50px' : '0';
        }

        return () => {
            map.removeControl(zoomControl);
        };
    }, [map, dataType]);

    return null;
};

const RiverMap = ({ selectedBasinMap, dataType, selectedTab }) => {
    const [loading, setLoading] = useState(0);
    const [stationData, setStationData] = useState(null);
    const [selectedStation, setSelectedStation] = useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [outerRings, setOuterRings] = useState([]);
    const [showTileOptions, setShowTileOptions] = useState(false);
    const [opacity, setOpacity] = useState(0.8);
    const loadingRef = React.useRef(loading);
    const basinsLayerRef = useRef(null);
    const riversLayerRef = useRef(null);
    const stationsLayerRef = useRef(null);
    const imageOverlayRef = useRef(null);
    const [tileLayer, setTileLayer] = useState(dataType === 'exceed' ? 'satellite' : 'terrain');

    useEffect(() => {
        setTileLayer(dataType === 'exceed' ? 'satellite' : 'terrain');
    }, [selectedTab, dataType]);

    useEffect(() => {
        outerRings.forEach(ring => ring.remove());
        setOuterRings([]);
    }, [selectedBasinMap]);

    useEffect(() => {
        loadingRef.current = loading;
    }, [loading]);

    useEffect(() => {
        setOpacity(0.8);
    }, [selectedBasinMap]);

    useEffect(() => {
        if (selectedMarker && selectedMarker._map) {
            selectedMarker.setStyle({
                radius: 5,
                fillColor: dataType === 'forecast' ? selectedMarker.feature.properties?.color : 'red',
                fillOpacity: opacity,
                opacity: 1,
            });

            outerRings.forEach(ring => ring.remove());
            setOuterRings([]);

            const map = selectedMarker._map;
            const newOuterRing = L.circleMarker(selectedMarker.getLatLng(), {
                radius: 10,
                fillColor: 'transparent',
                color: dataType === 'forecast' ? selectedMarker.feature.properties?.color : 'yellow',
                weight: 3,
                opacity: 1,
            }).addTo(map);

            selectedMarker.remove();
            selectedMarker.addTo(map);

            setOuterRings([newOuterRing]);
        }
    }, [selectedMarker, dataType, opacity]);

    useEffect(() => {
        setSelectedStation(null);
        setStationData(null);
        setSelectedMarker(null);
        const observer = new MutationObserver(() => {
            const paths = document.querySelectorAll('path.leaflet-interactive');
            paths.forEach((path) => {
                path.style.outline = 'none';
            });
        });

        const mapContainer = document.querySelector('.leaflet-container');
        if (mapContainer) {
            observer.observe(mapContainer, { childList: true, subtree: true });
        }

        return () => observer.disconnect();
    }, [selectedBasinMap]);

    // Update layer opacities when opacity changes
    useEffect(() => {
        if (dataType !== 'exceed') {
            if (basinsLayerRef.current) {
                basinsLayerRef.current.eachLayer((layer) => {
                    layer.setStyle({
                        fillOpacity: opacity,
                        opacity: 1,
                    });
                    if (layer._originalStyle) {
                        layer._originalStyle.fillOpacity = opacity;
                    }
                });
            }
            if (riversLayerRef.current) {
                riversLayerRef.current.eachLayer((layer) => {
                    layer.setStyle({
                        opacity: 1,
                    });
                });
            }
            if (stationsLayerRef.current) {
                stationsLayerRef.current.eachLayer((layer) => {
                    layer.setStyle({
                        fillOpacity: opacity,
                        opacity: 1,
                    });
                });
            }
        } else if (imageOverlayRef.current) {
            imageOverlayRef.current.setOpacity(opacity);
        }
    }, [opacity, dataType]);

    const { geojson_basins, geojson_rivers, geojson_stations } = selectedBasinMap || {
        geojson_basins: null,
        geojson_rivers: null,
        geojson_stations: null,
    };

    const handleStationClick = async (station, type) => {
        if (selectedStation?.properties?.station === station.properties.station) {
            return;
        }

        setSelectedStation(station);
        setLoading(prev => prev + 1);
        setStationData(null);

        try {
            let responses = {
                forecast1: null,
                forecast2: null,
                historic: null,
            };

            if (type === 'forecast') {
                const currentDate = new Date();
                const dynamicDate = currentDate.toISOString().split('T')[0];

                const payloads = [
                    { view: 1, station: station.properties.station, from_date: dynamicDate },
                    { view: 2, station: station.properties.station },
                ];

                let responseData = await Promise.all(
                    payloads.map(payload =>
                        fetch(`${envConfig.API_URL_P3}/fews/station_forecast`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(payload),
                        }).then(res => res.json())
                    )
                );

                responses.forecast1 = responseData[0].data;
                responses.forecast2 = responseData[1].data;
            } else {
                const payload = {
                    station: station.properties.station,
                    timescale: 'day',
                };

                const response = await fetch(`${envConfig.API_URL_P3}/fews/station_historic`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) throw new Error('Failed to fetch station data');

                let responseData = await response.json();
                responses.historic = responseData.data;
            }

            setStationData(responses);
        } catch (error) {
            console.error('Error:', error);
            setStationData(null);
        } finally {
            setLoading(prev => prev - 1);
        }
    };

    const base64ToDataUrl = (base64) => `data:image/png;base64,${base64}`;

    const toggleTileOptions = () => {
        setShowTileOptions((prev) => !prev);
    };

    const handleOpacityChange = (newOpacity) => {
        setOpacity(parseFloat(newOpacity));
    };

    const onEachFeature = (feature, layer) => {
        const baseStyle = {
            color: '#af0105',
            weight: 1,
            fillColor: dataType === 'exceed' ? 'transparent' : '#ffcccb',
            fillOpacity: opacity,
            opacity: 1,
        };

        layer._originalStyle = { ...baseStyle };

        const hoverStyle = {
            ...baseStyle,
            color: '#ff0000',
            fillColor: dataType === 'exceed' ? 'transparent' : '#ffe6e6',
        };

        layer.setStyle(baseStyle);

        layer.bindTooltip(() => {
            const basin = feature.properties.BASIN || 'Unnamed Basin';
            return basin;
        });

        if (layer._path && layer._path.classList.contains('leaflet-interactive')) {
            layer.on('click', function () {
                layer.setStyle({
                    stroke: false,
                    fillOpacity: opacity,
                    fillColor: baseStyle.fillColor,
                });
            });
        }

        if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
            layer.on('mouseover', function () {
                this.setStyle(hoverStyle);
            });

            layer.on('mouseout', function () {
                if (this._originalStyle) {
                    this.setStyle(this._originalStyle);
                    console.log('Restored to originalStyle:', this._originalStyle);
                }
            });
        } else if (feature.geometry.type === 'Point') {
            layer.bindTooltip('Point feature: ' + (feature.properties.BASIN || 'Unnamed Point'));
        }
    };

    return (
        <>
            <div style={{ height: '' }}>
                {showTileOptions && (
                    <div className="card legendCard four border-0 bg-transparent">
                        <div className="card-header bg-white px-1 pt-1 pb-1 border-0 d-flex align-items-center btnGroup">
                            {Object.entries(tileLayers).map(([key, { label, icon }]) => (
                                <button
                                    key={key}
                                    onClick={() => setTileLayer(key)}
                                    className={`btn border-0 ${tileLayer === key ? 'active' : ''}`}
                                    style={{ marginRight: '5px' }}
                                    aria-label={`Switch to ${label} map`}
                                    aria-pressed={tileLayer === key}
                                >
                                    <span>
                                        <img src={icon} loading="lazy" alt={`${label} map icon`} height="26px" />
                                    </span>{' '}
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <MapContainer
                    key={dataType}
                    center={[0.0, 20.0]}
                    zoom={4}
                    style={{ height: '80.3vh', width: '100%', marginTop: '0px', position: 'relative' }}
                    zoomControl={false}
                    preferCanvas={true}
                >
                    <AdjustZoomControl dataType={dataType} />

                    <TileLayer
                        url={tileLayers[tileLayer].url}
                        attribution={tileLayers[tileLayer].attribution}
                    />

                    {dataType !== 'exceed' && (
                        <>
                            <GeoJSON
                                key={JSON.stringify(geojson_basins) + '-basins'}
                                data={geojson_basins}
                                onEachFeature={(feature, layer) => onEachFeature(feature, layer)}
                                ref={basinsLayerRef}
                            />
                            {geojson_basins && <FitBounds geoJson={geojson_basins} dataType={dataType} />}
                            <GeoJSON
                                key={JSON.stringify(geojson_rivers) + '-rivers'}
                                data={geojson_rivers}
                                style={{ color: 'blue', weight: 1, opacity: 1 }}
                                ref={riversLayerRef}
                            />
                            <GeoJSON
                                key={JSON.stringify(geojson_stations) + '-stations'}
                                data={geojson_stations}
                                pointToLayer={(feature, latlng) => {
                                    const stationMarker = L.circleMarker(latlng, {
                                        radius: 5,
                                        fillColor: dataType === 'forecast' ? feature.properties?.color : 'red',
                                        color: 'darkred',
                                        weight: 1,
                                        opacity: 1,
                                        fillOpacity: opacity,
                                    });

                                    stationMarker.bindTooltip(
                                        `Station: ${feature.properties.name?.replace(/_/g, ' ')}<br/>ID: ${feature.properties.station}${dataType === 'forecast' ? `<br/>${feature.properties.label}` : ''}`,
                                        { permanent: false, direction: 'top' }
                                    );

                                    stationMarker.on('click', () => {
                                        if (loadingRef.current > 0) {
                                            return;
                                        }
                                        handleStationClick(feature, dataType);

                                        if (selectedMarker) {
                                            selectedMarker.setStyle({
                                                radius: 5,
                                                fillColor: dataType === 'forecast' ? feature.properties?.color : 'red',
                                                fillOpacity: opacity,
                                                opacity: 1,
                                            });
                                        }

                                        setSelectedMarker(stationMarker);
                                    });

                                    return stationMarker;
                                }}
                                ref={stationsLayerRef}
                            />
                        </>
                    )}

                    {dataType === 'exceed' && selectedBasinMap?.base64_layer && selectedBasinMap?.bbox && (
                        <>
                            <GeoJSON
                                key={JSON.stringify(selectedBasinMap?.geojson_basin) + '-basins'}
                                data={selectedBasinMap?.geojson_basin}
                                onEachFeature={(feature, layer) => onEachFeature(feature, layer)}
                                ref={basinsLayerRef}
                            />
                            {selectedBasinMap?.geojson_basin && <FitBounds geoJson={selectedBasinMap?.geojson_basin} dataType={dataType} />}
                            <ImageOverlay
                                url={base64ToDataUrl(selectedBasinMap.base64_layer)}
                                bounds={[
                                    [selectedBasinMap.bbox[1], selectedBasinMap.bbox[0]],
                                    [selectedBasinMap.bbox[3], selectedBasinMap.bbox[2]],
                                ]}
                                opacity={opacity}
                                ref={imageOverlayRef}
                            />
                        </>
                    )}
                </MapContainer>

                {(dataType === 'exceed' || dataType !== 'exceed') && selectedBasinMap && (
                    <Product3Legend
                        legendData={selectedBasinMap?.base64_legend}
                        onOpacityChange={handleOpacityChange}
                        dataType={dataType}
                    />
                )}

                {dataType !== 'exceed' && selectedBasinMap && selectedStation && (
                    <StationGraph
                        key={`${selectedStation?.properties?.station}-${dataType}`}
                        stationData={stationData}
                        selectedStation={selectedStation}
                        loading={loading}
                        dataType={dataType}
                    />
                )}
            </div>

            <div
                className="card legendCard ten border-0"
                style={{
                    backgroundImage: `url(${bgImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    fontSize: '12px !important',
                    fontWeight: '600 !important',
                    position: 'absolute',
                    bottom: '12px',
                    left: '2px',
                    cursor: 'pointer',
                    backgroundColor: 'transparent'
                }}
                onClick={toggleTileOptions}
            >
                <p className="mb-0 text-white">
                    <span><img src="../assets/images/layers1.png" /></span> Layers
                </p>
            </div>
        </>
    );
};

export default RiverMap;
